import { Input } from "@/components/ui/input";
import { Field, useFieldError } from "@/shared/forms/Field";
import { cn } from "@/shared/lib/utils";
import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

type FormPriceProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "onChange" | "value"
> & {
  name: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  requiredMark?: boolean;
  currencySymbol?: string;
};

export function FormPrice({
  name,
  label,
  hint,
  requiredMark,
  currencySymbol = "₭",
  step = 0.01,
  min = 0,
  className,
  ...rest
}: FormPriceProps) {
  const { control } = useFormContext();
  const error = useFieldError(name);

  return (
    <Field name={name} label={label} hint={hint} requiredMark={requiredMark}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground text-sm">
              {currencySymbol}
            </span>
            <Input
              id={name}
              type="number"
              step={step}
              min={min}
              aria-invalid={!!error}
              className={cn("pl-8 tabular-nums", className)}
              value={field.value ?? ""}
              onChange={(event) => {
                const next = event.target.value;
                field.onChange(
                  next === "" ? undefined : Number(next),
                );
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              {...rest}
            />
          </div>
        )}
      />
    </Field>
  );
}
